import { useState } from 'react'
import { useNavigate } from 'react-router'
import { CheckCheck } from 'lucide-react'
import { PageHeader } from '@/components/shared/PageHeader'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardTitle } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { cn } from '@/lib/utils'
import {
  useMarkAllNotificationsRead,
  useMarkNotificationsRead,
  useNotificationList,
  useNotificationPreferences,
  useSaveNotificationPreferences,
} from '@/hooks/useAdminNotifications'
import {
  NOTIFICATION_CATEGORIES,
  type AdminNotification,
  type NotificationCategory,
} from '@/lib/api/admin-notifications'
import { CATEGORY_LABELS, groupByDay, notificationHref } from '@/lib/notifications'
import { NotificationItem } from './NotificationItem'

const PAGE_SIZE = 30

export function NotificationsPage() {
  return (
    <div>
      <PageHeader
        title="Notifications"
        description="Billing, support, and growth events across HangarMax"
      />
      <Tabs defaultValue="feed">
        <TabsList className="mb-4">
          <TabsTrigger value="feed">Feed</TabsTrigger>
          <TabsTrigger value="preferences">Preferences</TabsTrigger>
        </TabsList>
        <TabsContent value="feed">
          <NotificationFeed />
        </TabsContent>
        <TabsContent value="preferences">
          <NotificationPreferencesPanel />
        </TabsContent>
      </Tabs>
    </div>
  )
}

// ─── Feed ────────────────────────────────────────────────────────────────────

function NotificationFeed() {
  const [category, setCategory] = useState<NotificationCategory | null>(null)
  const [unreadOnly, setUnreadOnly] = useState(false)
  const [page, setPage] = useState(1)
  const navigate = useNavigate()

  const markRead = useMarkNotificationsRead()
  const markAllRead = useMarkAllNotificationsRead()

  const { data, isLoading } = useNotificationList({
    page,
    pageSize: PAGE_SIZE,
    category,
    unreadOnly,
  })

  const notifications = data?.notifications ?? []
  const total = data?.total ?? 0
  const pageCount = Math.max(Math.ceil(total / PAGE_SIZE), 1)
  const groups = groupByDay(notifications)

  function select(n: AdminNotification) {
    if (!n.is_read) markRead.mutate([n.id])
    const href = notificationHref(n)
    if (href) navigate(href)
  }

  /** Any filter change invalidates the current page number. */
  function applyCategory(next: NotificationCategory | null) {
    setCategory(next)
    setPage(1)
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <FilterChip active={category === null} onClick={() => applyCategory(null)}>
          All
        </FilterChip>
        {NOTIFICATION_CATEGORIES.map((c) => (
          <FilterChip key={c} active={category === c} onClick={() => applyCategory(c)}>
            {CATEGORY_LABELS[c]}
          </FilterChip>
        ))}

        <div className="ml-2 flex items-center gap-2">
          <Switch
            id="unread-only"
            checked={unreadOnly}
            onCheckedChange={(v) => {
              setUnreadOnly(v)
              setPage(1)
            }}
          />
          <Label htmlFor="unread-only" className="text-sm text-muted-foreground">
            Unread only
          </Label>
        </div>

        <Button
          variant="outline"
          size="sm"
          className="ml-auto"
          onClick={() => markAllRead.mutate(category)}
          disabled={markAllRead.isPending}
        >
          <CheckCheck className="mr-1.5 h-3.5 w-3.5" />
          Mark all read
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full" />
          ))}
        </div>
      ) : notifications.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-sm text-muted-foreground">
            {unreadOnly ? 'Nothing unread.' : 'No notifications yet.'}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-5">
          {groups.map((group) => (
            <div key={group.label}>
              <h2 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {group.label}
              </h2>
              <Card className="overflow-hidden py-0">
                <div className="divide-y">
                  {group.items.map((n) => (
                    <NotificationItem key={n.id} notification={n} onSelect={select} />
                  ))}
                </div>
              </Card>
            </div>
          ))}
        </div>
      )}

      {pageCount > 1 && (
        <div className="flex items-center justify-end gap-2">
          <span className="text-sm text-muted-foreground">
            Page {page} of {pageCount}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.max(p - 1, 1))}
            disabled={page <= 1}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.min(p + 1, pageCount))}
            disabled={page >= pageCount}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  )
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-full border px-3 py-1 text-sm transition-colors',
        active
          ? 'border-primary bg-primary/10 font-medium text-primary'
          : 'text-muted-foreground hover:bg-muted',
      )}
    >
      {children}
    </button>
  )
}

// ─── Preferences ─────────────────────────────────────────────────────────────

function NotificationPreferencesPanel() {
  const { data: preferences, isLoading } = useNotificationPreferences()
  const save = useSaveNotificationPreferences()

  if (isLoading || !preferences) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-20 w-full" />
        ))}
      </div>
    )
  }

  function toggle(category: NotificationCategory) {
    if (!preferences) return
    save.mutate({
      ...preferences,
      categories: {
        ...preferences.categories,
        [category]: !preferences.categories[category],
      },
    })
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Muting a category hides it from the bell and the feed for you only. Nothing is
        deleted — turning it back on restores the full history.
      </p>
      {NOTIFICATION_CATEGORIES.map((c) => (
        <Card key={c}>
          <CardContent className="flex items-center justify-between gap-4 py-4">
            <div>
              <CardTitle className="text-base">{CATEGORY_LABELS[c]}</CardTitle>
              <CardDescription>{CATEGORY_DESCRIPTIONS[c]}</CardDescription>
            </div>
            <Switch
              data-testid={`notification-pref-${c}`}
              checked={preferences.categories[c] !== false}
              onCheckedChange={() => toggle(c)}
              disabled={save.isPending}
            />
          </CardContent>
        </Card>
      ))}
    </div>
  )
}

const CATEGORY_DESCRIPTIONS: Record<NotificationCategory, string> = {
  billing: 'New subscriptions, plan changes, failed payments, cancellations, disputes and refunds.',
  support: 'New support threads, replies from users, and QA feedback submissions.',
  growth: 'Daily signup counts, first layouts created, and account deletions.',
}
